import React from 'react';

import { Divider } from '@reckon-web/divider';
import { Flex } from '@reckon-web/flex';
import { makeId } from '@reckon-web/utils';

import {
  GetUserFilterListResult,
  useGetUserFilterList,
} from '../../data/useGetUserFilterList';
import { useOpenJobDialog } from '../../hooks/use-open-job-dialog';
import { useOpenTaskDialog } from '../../hooks/use-open-task-dialog';
import { useSaveUserFilter } from '../../hooks/use-save-user-filter';
import { useQueryJobsWithTasks } from '../../hooks/use-query-jobs-with-tasks';
import { useQueryTasksWithJob } from '../../hooks/use-query-tasks-with-job';
import { useExportToCsv } from '../../hooks/use-export-to-csv';
import { Block } from '../common/block';
import { useVisibleProperties } from '../common/visible-properties';
import { useVisibleFilters } from '../common/visible-filters';
import { useOpenNewJobDialog } from '../job/new-job-dialog';
import { useTaskDrawer } from '../task/task-drawer-context';
import { useGetJobStatusList } from '../../data/useGetJobStatusList';
import { Task } from '../../hooks/use-query-tasks';

import { WorkViewEnum } from './types/work-view-enum';
import { Jobs } from './jobs';
import { Tasks } from './tasks/Tasks';
import { Filter as JobsFilter } from './jobs/filter';
import { Filter as TasksFilter } from './tasks/filter';
import { JobsOrTasks, JobsOrTasksToggle } from './jobs-or-tasks';
import { KanbanOrList } from './kanban-or-list';
import { SortType } from './sort';
import { DataControls } from './data-controls';
import {
  JobsFilter as JobsFilterType,
  TasksFilter as TasksFilterType,
  emptyJobsFilter,
  emptyTasksFilter,
  equals,
  getActiveJobsFilter,
  getActiveTasksFilter,
  parseJobBoardVisibleProperties,
  parseJobListVisibleProperties,
  parseJobsFilter,
  parseTaskBoardVisibleProperties,
  parseTaskListVisibleProperties,
  parseTasksFilter,
  parseVisibleFilters,
  serialiseJobBoardVisibleProperties,
  serialiseJobListVisibleProperties,
  serialiseJobsFilter,
  serialiseTaskBoardVisibleProperties,
  serialiseTaskListVisibleProperties,
  serialiseTasksFilter,
  serialiseVisibleFilters,
} from './filter-helpers';
import { jobsToCsv, tasksToCsv } from './export-helpers';

const sharedFilterKeys: (keyof JobsFilterType & keyof TasksFilterType)[] = [
  'myWork',
  'text',
  'clients',
  'dueDateOverdue',
  'dueDateBegin',
  'dueDateEnd',
  'familyGroupIds',
  'completedDateBegin',
  'completedDateEnd',
];

/* TODO Workflow-400 get this in as a parameter */
const baseTestId = `work`;
export const Work = () => {
  const [visibleProperties, setVisibleProperties] = useVisibleProperties();
  const [visibleFilters, setVisibleFilters] = useVisibleFilters();

  const openJobDialog = useOpenJobDialog();
  const openTaskDialog = useOpenTaskDialog();
  const openNewJobDialog = useOpenNewJobDialog();
  const { openCreate } = useTaskDrawer();
  const [SaveUserFilterDialog, _saveUserFilter] = useSaveUserFilter();
  const [ExportToCsvDialog, _exportToCsv] = useExportToCsv();

  const { data: userFilters, refetch: refetchUserFilters } =
    useGetUserFilterList();

  const { data: jobStatuses } = useGetJobStatusList();

  const [jobsOrTasks, setJobsOrTasks] = React.useState<JobsOrTasks>('jobs');

  const [defaultKanbanOrList, setDefaultKanbanOrList] =
    React.useState<KanbanOrList>('kanban');
  const [kanbanOrList, setKanbanOrList] =
    React.useState<KanbanOrList>(defaultKanbanOrList);
  const [sort, setSort] = React.useState<SortType>('Due Date (earliest first)');

  // Dans notes: This is the actual ID of the filter. Simple state
  // This is set when:
  //   the clear filter button is clicked
  //   a filter is applied
  //   a new filter is saved
  // Changes to this reevaluates the lastAppliedJobsUserFilter memo
  const [lastAppliedJobsUserFilterId, setLastAppliedJobsUserFilterId] =
    React.useState<string>();
  const [lastAppliedTasksUserFilterId, setLastAppliedTasksUserFilterId] =
    React.useState<string>();

  // Dans notes. This is the looked up object of the user filter. Has the name and unparsed json
  // This is used in a few spots
  // This could also be re-evaluated in a useEffect when lastAppliedJobsUserFilterId changes
  // if we wanted to get some async in and parse up the json so we can do job status work for old filters to new filters
  const lastAppliedJobsUserFilter = React.useMemo<
    GetUserFilterListResult | undefined
  >(
    () =>
      lastAppliedJobsUserFilterId
        ? userFilters?.find(
            ({ cloudId }) => cloudId === lastAppliedJobsUserFilterId
          )
        : undefined,
    [userFilters, lastAppliedJobsUserFilterId]
  );
  const lastAppliedTasksUserFilter = React.useMemo<
    GetUserFilterListResult | undefined
  >(
    () =>
      lastAppliedTasksUserFilterId
        ? userFilters?.find(
            ({ cloudId }) => cloudId === lastAppliedTasksUserFilterId
          )
        : undefined,
    [userFilters, lastAppliedTasksUserFilterId]
  );

  const [defaultJobsFilter, setDefaultJobFilter] =
    React.useState<JobsFilterType>({
      ...emptyJobsFilter,
      myWork: true,
    });

  const [defaultTasksFilter, setDefaultTaskFilter] =
    React.useState<TasksFilterType>({
      ...emptyTasksFilter,
      myWork: true,
    });

  // Dans notes: This is the raw filter values for the query jobs.
  // It has all the values, including stuff for filter controls that are not visible
  const [jobsFilter, _setJobsFilter] =
    React.useState<JobsFilterType>(defaultJobsFilter);
  const [tasksFilter, _setTasksFilter] =
    React.useState<TasksFilterType>(defaultTasksFilter);

  React.useMemo(() => {
    if (!!lastAppliedJobsUserFilter) {
      const jobsFilters = parseJobsFilter(
        JSON.parse(lastAppliedJobsUserFilter?.filterDefinition || ''),
        jobStatuses || []
      );
      setDefaultJobFilter(jobsFilters);

      if (
        lastAppliedJobsUserFilter.workView === WorkViewEnum.valuesEnum.JobBoard
      ) {
        setDefaultKanbanOrList('kanban');
      }

      if (
        lastAppliedJobsUserFilter.workView === WorkViewEnum.valuesEnum.JobList
      ) {
        setDefaultKanbanOrList('list');
      }
    }
  }, [jobStatuses, lastAppliedJobsUserFilter]);

  React.useMemo(() => {
    if (!!lastAppliedTasksUserFilter) {
      const tasksFilters = parseTasksFilter(
        JSON.parse(lastAppliedTasksUserFilter?.filterDefinition || '')
      );
      setDefaultTaskFilter(tasksFilters);

      if (
        lastAppliedTasksUserFilter.workView ===
        WorkViewEnum.valuesEnum.TaskBoard
      ) {
        setDefaultKanbanOrList('kanban');
      }

      if (
        lastAppliedTasksUserFilter.workView === WorkViewEnum.valuesEnum.TaskList
      ) {
        setDefaultKanbanOrList('list');
      }
    }
  }, [lastAppliedTasksUserFilter]);

  const setJobsFilter = (value: Partial<JobsFilterType>) => {
    _setJobsFilter((prev) => ({ ...prev, ...value }));
    _setTasksFilter((prev) => ({
      ...prev,
      ...Object.fromEntries(
        Object.entries(value).filter(([k]) =>
          sharedFilterKeys.includes(k as any)
        )
      ),
    }));
  };

  const setTasksFilter = (value: Partial<TasksFilterType>) => {
    _setTasksFilter((prev) => ({ ...prev, ...value }));
    _setJobsFilter((prev) => ({
      ...prev,
      ...Object.fromEntries(
        Object.entries(value).filter(([k]) =>
          sharedFilterKeys.includes(k as any)
        )
      ),
    }));
  };

  const clearFilterChanges = () => {
    _setJobsFilter(defaultJobsFilter);
    _setTasksFilter(defaultTasksFilter);
    setKanbanOrList(defaultKanbanOrList);
  };

  // Dans notes: This is the actual values passed to queryJobs, with only the visible filter values in it
  const activeJobsFilter = React.useMemo(
    () => getActiveJobsFilter(jobsFilter, visibleFilters),
    [jobsFilter, visibleFilters]
  );
  const activeTasksFilter = React.useMemo(
    () => getActiveTasksFilter(tasksFilter, visibleFilters),
    [tasksFilter, visibleFilters]
  );

  const jobsQueryResult = useQueryJobsWithTasks({
    variables: activeJobsFilter,
    skip: jobsOrTasks !== 'jobs',
  });

  const tasksQueryResult = useQueryTasksWithJob({
    variables: activeTasksFilter,
    skip: jobsOrTasks !== 'tasks',
  });

  const hasJobsFilterChanged: boolean = React.useMemo(() => {
    return (
      !equals(activeJobsFilter, defaultJobsFilter) ||
      !equals(kanbanOrList, defaultKanbanOrList)
    );
  }, [activeJobsFilter, defaultJobsFilter, defaultKanbanOrList, kanbanOrList]);

  const hasTasksFilterChanged: boolean = React.useMemo(() => {
    return (
      !equals(activeTasksFilter, defaultTasksFilter) ||
      !equals(kanbanOrList, defaultKanbanOrList)
    );
  }, [
    activeTasksFilter,
    defaultKanbanOrList,
    defaultTasksFilter,
    kanbanOrList,
  ]);

  // Dans notes: This is used when we need to know that the user has applied a filter, and that they haven't changed the criteria
  // It really SHOULD NOT be doing the parse in the middle there, that feels like something that should happen when lastAppliedJobsUserFilter changes
  // but it got the job done at the time
  const appliedJobsUserFilter: GetUserFilterListResult | undefined =
    React.useMemo(() => {
      return lastAppliedJobsUserFilter &&
        equals(
          activeJobsFilter,
          parseJobsFilter(
            JSON.parse(lastAppliedJobsUserFilter.filterDefinition!),
            jobStatuses || [] // by the time we get to this, we will have statuses loaded
          )
        )
        ? lastAppliedJobsUserFilter
        : undefined;
    }, [lastAppliedJobsUserFilter, activeJobsFilter, jobStatuses]);

  const appliedTasksUserFilter: GetUserFilterListResult | undefined =
    React.useMemo(() => {
      return lastAppliedTasksUserFilter &&
        equals(
          activeTasksFilter,
          parseTasksFilter(
            JSON.parse(lastAppliedTasksUserFilter.filterDefinition!)
          )
        )
        ? lastAppliedTasksUserFilter
        : undefined;
    }, [lastAppliedTasksUserFilter, activeTasksFilter]);

  const applyUserFilter = React.useCallback(
    (userFilter: GetUserFilterListResult) => {
      const json = JSON.parse(userFilter.filterDefinition!);
      const userFilterCloudId = userFilter.cloudId || '';

      switch (userFilter.workView) {
        case WorkViewEnum.valuesEnum.JobBoard: {
          setJobsOrTasks('jobs');
          setDefaultKanbanOrList('kanban');
          setVisibleFilters(parseVisibleFilters(json));
          setJobsFilter(parseJobsFilter(json, jobStatuses || []));
          setLastAppliedJobsUserFilterId(userFilter.cloudId || '');
          setVisibleProperties((prev) => ({
            ...prev,
            jobBoard: parseJobBoardVisibleProperties(json),
          }));
          break;
        }
        case WorkViewEnum.valuesEnum.JobList: {
          setJobsOrTasks('jobs');
          setDefaultKanbanOrList('list');
          setVisibleFilters(parseVisibleFilters(json));
          setJobsFilter(parseJobsFilter(json, jobStatuses || []));
          setLastAppliedJobsUserFilterId(userFilterCloudId);
          setVisibleProperties((prev) => ({
            ...prev,
            jobList: parseJobListVisibleProperties(json),
          }));
          break;
        }
        case WorkViewEnum.valuesEnum.TaskBoard: {
          setJobsOrTasks('tasks');
          setDefaultKanbanOrList('kanban');
          setVisibleFilters(parseVisibleFilters(json));
          setTasksFilter(parseTasksFilter(json));
          setLastAppliedTasksUserFilterId(userFilterCloudId);
          setVisibleProperties((prev) => ({
            ...prev,
            taskBoard: parseTaskBoardVisibleProperties(json),
          }));
          break;
        }
        case WorkViewEnum.valuesEnum.TaskList: {
          setJobsOrTasks('tasks');
          setDefaultKanbanOrList('list');
          setVisibleFilters(parseVisibleFilters(json));
          setTasksFilter(parseTasksFilter(json));
          setLastAppliedTasksUserFilterId(userFilterCloudId);
          setVisibleProperties((prev) => ({
            ...prev,
            taskList: parseTaskListVisibleProperties(json),
          }));
          break;
        }
      }
    },
    [setVisibleProperties, setVisibleFilters, jobStatuses]
  );

  const saveUserFilter = React.useCallback(async () => {
    if (jobsOrTasks === 'jobs') {
      _saveUserFilter(
        {
          cloudId: lastAppliedJobsUserFilter?.cloudId || '',
          name: lastAppliedJobsUserFilter?.name,
          workView: (() => {
            if (kanbanOrList === 'kanban')
              return WorkViewEnum.valuesEnum.JobBoard;
            if (kanbanOrList === 'list') return WorkViewEnum.valuesEnum.JobList;
          })(),
          filterDefinition: JSON.stringify({
            version: 1,
            selectedSortOrder: undefined,
            ...serialiseJobsFilter(activeJobsFilter),
            toggleableProperties: (() => {
              if (kanbanOrList === 'kanban')
                return serialiseJobBoardVisibleProperties(visibleProperties);
              if (kanbanOrList === 'list')
                return serialiseJobListVisibleProperties(visibleProperties);
            })(),
            filterableProperties: serialiseVisibleFilters(visibleFilters),
          }),
          isDefault: lastAppliedJobsUserFilter?.isDefault,
        },
        async (cloudId) => {
          await refetchUserFilters();
          setLastAppliedJobsUserFilterId(cloudId);
        }
      );
    }

    if (jobsOrTasks === 'tasks') {
      _saveUserFilter(
        {
          cloudId: lastAppliedTasksUserFilter?.cloudId || '',
          name: lastAppliedTasksUserFilter?.name,
          workView: (() => {
            if (kanbanOrList === 'kanban')
              return WorkViewEnum.valuesEnum.TaskBoard;
            if (kanbanOrList === 'list')
              return WorkViewEnum.valuesEnum.TaskList;
          })(),
          filterDefinition: JSON.stringify({
            version: 1,
            selectedSortOrder: undefined,
            ...serialiseTasksFilter(activeTasksFilter),
            toggleableProperties: (() => {
              if (kanbanOrList === 'kanban')
                return serialiseTaskBoardVisibleProperties(visibleProperties);
              if (kanbanOrList === 'list')
                return serialiseTaskListVisibleProperties(visibleProperties);
            })(),
            filterableProperties: serialiseVisibleFilters(visibleFilters),
          }),
          isDefault: lastAppliedTasksUserFilter?.isDefault,
        },
        async (cloudId) => {
          await refetchUserFilters();
          setLastAppliedTasksUserFilterId(cloudId);
        }
      );
    }
  }, [
    _saveUserFilter,
    refetchUserFilters,
    jobsOrTasks,
    kanbanOrList,
    visibleProperties,
    visibleFilters,
    lastAppliedJobsUserFilter,
    lastAppliedTasksUserFilter,
    activeJobsFilter,
    activeTasksFilter,
  ]);

  const exportToCsv = React.useCallback(() => {
    if (jobsOrTasks === 'jobs' && jobsQueryResult.data) {
      _exportToCsv({
        exportWorkType: 'job',
        count: jobsQueryResult.data.length,
        csv: jobsToCsv(
          (() => {
            switch (kanbanOrList) {
              case 'kanban':
                return visibleProperties.jobBoard;
              case 'list':
                return visibleProperties.jobList;
            }
          })()
        )(jobsQueryResult.data),
        filterName: appliedJobsUserFilter?.name ?? undefined,
      });
    }
    if (jobsOrTasks === 'tasks' && tasksQueryResult.data) {
      const tasksQueryResultData = tasksQueryResult.data as Task[];

      _exportToCsv({
        exportWorkType: 'task',
        count: tasksQueryResult.data.length,
        csv: tasksToCsv(
          (() => {
            switch (kanbanOrList) {
              case 'kanban':
                return visibleProperties.taskBoard;
              case 'list':
                return visibleProperties.taskList;
            }
          })()
        )(tasksQueryResultData),
        filterName: appliedTasksUserFilter?.name ?? undefined,
      });
    }
  }, [
    visibleProperties,
    _exportToCsv,
    jobsOrTasks,
    kanbanOrList,
    jobsQueryResult,
    tasksQueryResult,
    appliedJobsUserFilter,
    appliedTasksUserFilter,
  ]);

  const canExport =
    (jobsOrTasks === 'jobs' &&
      jobsQueryResult.data &&
      jobsQueryResult.data?.length > 0) ||
    (jobsOrTasks === 'tasks' &&
      tasksQueryResult.data &&
      tasksQueryResult.data?.length > 0);

  // FIXME: avoid `css` prop. Make configurable with props.
  return (
    <>
      <Block css={{ display: 'flex', flexShrink: 0 }}>
        <Flex gap="small">
          <Flex gap="small" paddingY="medium">
            <JobsOrTasksToggle
              jobsOrTasks={jobsOrTasks}
              setJobsOrTasks={setJobsOrTasks}
              testId={makeId(baseTestId, 'jobs-or-tasks-toggle')}
            />
            {jobsOrTasks === 'jobs' && (
              <JobsFilter
                filter={jobsFilter}
                setFilter={setJobsFilter}
                hasFiltersChanged={hasJobsFilterChanged}
                clearFilter={clearFilterChanges}
                saveUserFilter={saveUserFilter}
                testId={makeId(baseTestId, 'jobs-filter')}
              />
            )}
            {jobsOrTasks === 'tasks' && (
              <TasksFilter
                filter={tasksFilter}
                setFilter={setTasksFilter}
                hasFiltersChanged={hasTasksFilterChanged}
                clearFilter={clearFilterChanges}
                saveUserFilter={saveUserFilter}
                testId={makeId(baseTestId, 'task-filter')}
              />
            )}
          </Flex>
          <Flex flexGrow={1} />
          <Divider vertical />
          <Flex gap="small" paddingY="medium" paddingLeft="xsmall">
            <DataControls
              showFilterIcon={true}
              jobsOrTasks={jobsOrTasks}
              kanbanOrList={kanbanOrList}
              setKanbanOrList={setKanbanOrList}
              sort={sort}
              setSort={setSort}
              exportToCsv={exportToCsv}
              userFilters={userFilters}
              applyUserFilter={applyUserFilter}
              canExport={canExport}
              testId={makeId(baseTestId, 'data-controls')}
            />
          </Flex>
        </Flex>
      </Block>
      <Divider />
      <Block css={{ display: 'flex', flexGrow: 1, overflow: 'auto' }}>
        {jobsOrTasks === 'jobs' && (
          <Jobs
            kanbanOrList={kanbanOrList}
            queryResult={jobsQueryResult}
            filtered={appliedJobsUserFilter?.name ?? hasJobsFilterChanged}
            clearFilter={clearFilterChanges}
            sort={sort}
            onClickJob={openJobDialog}
            onClickCreateJob={openNewJobDialog}
            testId={makeId(baseTestId, 'jobs')}
          />
        )}
        {jobsOrTasks === 'tasks' && (
          <Tasks
            kanbanOrList={kanbanOrList}
            queryResult={tasksQueryResult}
            filtered={appliedTasksUserFilter?.name ?? hasTasksFilterChanged}
            clearFilter={clearFilterChanges}
            sort={sort}
            onClickJob={openJobDialog}
            onClickTask={openTaskDialog}
            onClickCreateTask={openCreate}
            testId={makeId(baseTestId, 'tasks')}
          />
        )}
      </Block>
      <SaveUserFilterDialog />
      <ExportToCsvDialog />
    </>
  );
};
